import { useState } from 'react';
import { motion } from 'framer-motion';

const handleWhatsAppClick = () => {
  if (typeof (window as Window & { gtag?: (...args: unknown[]) => void }).gtag === 'function') {
    (window as Window & { gtag: (...args: unknown[]) => void }).gtag('event', 'conversion', {
      send_to: 'AW-17106059641/wLJKCOOCiIscEPmC59w_',
      value: 1.0,
      currency: 'CLP',
    });
  }
};

const fireFormConversion = () => {
  if (typeof (window as Window & { gtag?: (...args: unknown[]) => void }).gtag === 'function') {
    (window as Window & { gtag: (...args: unknown[]) => void }).gtag('event', 'conversion', {
      send_to: 'AW-17106059641/i4UoCPuwkYscEPmC59w_',
      value: 1.0,
      currency: 'CLP',
    });
  }
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6m4veh4a3uojp5fgjg0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        fireFormConversion();
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          mensaje: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-yellow/20 rounded-full blur-3xl -z-10"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Left Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">
              Hablemos de tu proyecto legal
            </h2>
            <p className="text-lg text-gray-medium mb-8 leading-relaxed">
              Cuéntanos qué necesitas y te ayudaremos a encontrar la mejor solución para tu empresa o emprendimiento.
            </p>

            {/* Contact Methods */}
            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-yellow rounded-xl flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-black"></i>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-1">Email</h4>
                  <a href="mailto:contacto@balachile.com" className="text-gray-medium hover:text-primary-yellow transition-colors cursor-pointer">
                    contacto@balachile.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-xl flex-shrink-0">
                  <i className="ri-whatsapp-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-1">WhatsApp</h4>
                  <a
                    href="https://wa.me/56942215553"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    onClick={handleWhatsAppClick}
                    className="inline-block bg-primary-yellow text-black px-6 py-2 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer mt-2"
                  >
                    Chatear ahora
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-yellow rounded-xl flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-black"></i>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-1">Teléfono</h4>
                  <p className="text-gray-medium">+56 9 8371 8012</p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-light rounded-2xl p-6">
              <h4 className="font-bold text-black mb-3 flex items-center">
                <i className="ri-time-line text-primary-yellow mr-2"></i>
                Horarios de atención
              </h4>
              <p className="text-gray-medium text-sm">
                Lunes a Viernes: 9:00 - 18:00<br />
                Sábados: 10:00 - 14:00
              </p>
            </div>

            {/* Response Badge */}
            <div className="mt-6 inline-flex items-center space-x-2 bg-primary-yellow text-black px-4 py-2 rounded-full text-sm font-bold">
              <i className="ri-time-line"></i>
              <span>Respuesta en 24 horas</span>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <form
              id="contacto-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-gray-light rounded-3xl p-8 lg:p-10 shadow-xl"
            >
              <div className="space-y-6">
                {/* Nombre */}
                <div>
                  <label htmlFor="nombre" className="block text-sm font-bold text-black mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary-yellow focus:outline-none transition-colors text-black"
                    placeholder="Tu nombre"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-black mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary-yellow focus:outline-none transition-colors text-black"
                    placeholder="tu@email.com"
                  />
                </div>

                {/* Teléfono */}
                <div>
                  <label htmlFor="telefono" className="block text-sm font-bold text-black mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary-yellow focus:outline-none transition-colors text-black"
                    placeholder="+56 9 1234 5678"
                  />
                </div>

                {/* Tipo de servicio */}
                <div>
                  <label htmlFor="servicio" className="block text-sm font-bold text-black mb-2">
                    Tipo de servicio *
                  </label>
                  <div className="relative">
                    <select
                      id="servicio"
                      name="servicio"
                      value={formData.servicio}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary-yellow focus:outline-none transition-colors appearance-none text-black cursor-pointer"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="Redacción de contratos">Redacción de contratos</option>
                      <option value="Revisión de contratos">Revisión de contratos</option>
                      <option value="Constitución de empresas SpA">Constitución de empresas SpA</option>
                      <option value="Registro de marca">Registro de marca</option>
                      <option value="Trámites empresariales">Trámites empresariales</option>
                      <option value="Gestiones notariales">Gestiones notariales</option>
                      <option value="Otro">Otro</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 transform -translate-y-1/2 text-primary-yellow text-xl pointer-events-none"></i>
                  </div>
                </div>

                {/* Mensaje */}
                <div>
                  <label htmlFor="mensaje" className="block text-sm font-bold text-black mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-primary-yellow focus:outline-none transition-colors resize-none text-black"
                    placeholder="Cuéntanos sobre tu necesidad legal..."
                  ></textarea>
                  <p className="text-xs text-gray-medium mt-1">
                    Máximo 500 caracteres ({formData.mensaje.length}/500)
                  </p>
                </div>

                {/* File Upload Area */}
                <div>
                  <label className="block text-sm font-bold text-black mb-2">
                    Adjuntar documentos (opcional)
                  </label>
                  <div className="border-2 border-dashed border-primary-yellow rounded-lg p-8 text-center hover:bg-yellow-50 transition-colors cursor-pointer">
                    <i className="ri-upload-cloud-line text-5xl text-primary-yellow mb-3"></i>
                    <p className="text-gray-dark font-medium mb-1">
                      Arrastra archivos o haz clic para subir
                    </p>
                    <p className="text-xs text-gray-medium">
                      No recopilable en esta versión
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-yellow text-black py-4 rounded-full font-bold text-lg hover:bg-yellow-400 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar consulta'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-100 border-2 border-green-500 text-green-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                    <i className="ri-checkbox-circle-line text-xl"></i>
                    <span>¡Mensaje enviado con éxito! Te contactaremos pronto.</span>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-100 border-2 border-red-500 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                    <i className="ri-error-warning-line text-xl"></i>
                    <span>Error al enviar. Por favor intenta nuevamente.</span>
                  </div>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;